<template>
  <component :is="layout">
    <router-view />
  </component>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import DefaultLayout from './components/layout/DefaultLayout.vue'

export default {
  name: 'App',
  components: {
    DefaultLayout
  },
  setup() {
    const route = useRoute()
    
    const layout = computed(() => {
      // Use blank layout for login page
      if (route.path === '/login') {
        return 'v-app'
      }
      // Use default layout for authenticated pages
      return 'default-layout'
    })

    return {
      layout
    }
  }
}
</script>

<style>
/* Force RTL globally */
* {
  direction: rtl !important;
}

html {
  direction: rtl !important;
}

body {
  direction: rtl !important;
  font-family: 'Beiruti', sans-serif !important;
}

.v-application {
  font-family: 'Beiruti', sans-serif !important;
  direction: rtl !important;
}

.v-application * {
  font-family: 'Beiruti', sans-serif !important;
  letter-spacing: 0 !important;
}

/* Remove letter spacing from specific Vuetify components */
.v-toolbar-title,
.v-card-title,
.v-card-text,
.v-list-item-title,
.v-list-item-subtitle,
.v-btn,
.v-tab,
.v-label,
.v-messages__message {
  letter-spacing: 0 !important;
}

/* Remove letter spacing from headings */
.text-h1,
.text-h2,
.text-h3,
.text-h4,
.text-h5,
.text-h6,
.text-subtitle-1,
.text-subtitle-2 {
  letter-spacing: 0 !important;
}

/* Fix alignment for icons in RTL */
.v-list-item__prepend > .v-icon {
  margin-inline-end: 16px !important;
  margin-inline-start: 8px !important;
}

/* Fix alignment for buttons in RTL */
.v-btn__prepend {
  margin-inline-end: 8px;
  margin-inline-start: -4px;
}

/* Fix data table alignment */
.v-data-table {
  text-align: right;
}

/* Fix dialog alignment */
.v-dialog {
  text-align: right;
}

/* Fix input icons alignment */
.v-field__prepend-inner {
  margin-inline-start: 0;
  margin-inline-end: 8px;
}

.v-field__append-inner {
  margin-inline-end: 0;
  margin-inline-start: 8px;
}

/* Fix card alignment */
.v-card {
  direction: rtl !important;
}

.v-card-title {
  direction: rtl !important;
  text-align: right !important;
}

.v-card-text {
  text-align: right !important;
  direction: rtl !important;
}

.v-card-subtitle {
  direction: rtl !important;
  text-align: right !important;
}

/* Fix form validation messages */
.v-messages {
  text-align: right;
}

/* Fix navigation drawer slide direction */
.v-navigation-drawer--right {
  transform: translateX(100%);
}

.v-navigation-drawer--right.v-navigation-drawer--is-active {
  transform: translateX(0);
}

/* Fix list item alignment */
.v-list-item {
  padding-inline-start: 16px;
  padding-inline-end: 16px;
}

.v-list-item__content {
  text-align: right;
}

/* Fix data table headers and content alignment */
.v-data-table {
  direction: rtl !important;
}

.v-data-table table {
  direction: rtl !important;
}

.v-data-table__th {
  text-align: right !important;
  direction: rtl !important;
}

.v-data-table__td {
  text-align: right !important;
  direction: rtl !important;
}

.v-data-table-header__content {
  text-align: right !important;
  direction: rtl !important;
}

/* Fix data table actions alignment */
.v-data-table__td:last-child {
  text-align: center !important;
}

/* Fix data table footer */
.v-data-table-footer {
  direction: rtl !important;
}

.v-data-table-footer__items-per-page {
  direction: rtl !important;
}

.v-data-table-footer__pagination {
  direction: rtl !important;
}

/* Fix button group alignment */
.d-flex.ga-2 {
  justify-content: center;
}

/* Fix chip alignment in RTL */
.v-chip {
  margin-inline-start: 4px;
  margin-inline-end: 4px;
}

/* Fix v-select and v-text-field icons */
.v-select,
.v-text-field,
.v-textarea,
.v-file-input {
  direction: rtl !important;
}

.v-field {
  direction: rtl !important;
}

.v-field__input {
  direction: rtl !important;
  text-align: right !important;
}

.v-select .v-field__append-inner,
.v-text-field .v-field__append-inner {
  margin-inline-end: 0;
  margin-inline-start: 8px;
}

.v-select .v-field__prepend-inner,
.v-text-field .v-field__prepend-inner {
  margin-inline-start: 0;
  margin-inline-end: 8px;
}

/* Fix labels */
.v-label {
  direction: rtl !important;
  text-align: right !important;
}

.v-field-label {
  direction: rtl !important;
  text-align: right !important;
}

/* Fix file input icon alignment */
.v-file-input .v-field__prepend-inner {
  margin-inline-start: 0;
  margin-inline-end: 8px;
}

/* Fix alert icon alignment */
.v-alert .v-alert__prepend {
  margin-inline-end: 12px;
  margin-inline-start: 0;
}

/* Fix checkbox alignment */
.v-checkbox .v-label {
  margin-inline-start: 8px;
  margin-inline-end: 0;
}

/* Fix card title icons */
.v-card-title .v-icon {
  margin-inline-end: 8px;
  margin-inline-start: 0;
}

/* Fix navigation drawer position */
.v-navigation-drawer--right {
  right: 0 !important;
  left: auto !important;
}

/* Fix dialog content alignment */
.v-dialog .v-card-text {
  text-align: right;
  direction: rtl;
}

/* Fix avatar positioning in profile */
.text-center .v-avatar {
  margin: 0 auto;
}

/* Fix button icons in RTL */
.v-btn .v-icon--start {
  margin-inline-end: 8px;
  margin-inline-start: -4px;
}

.v-btn .v-icon--end {
  margin-inline-start: 8px;
  margin-inline-end: -4px;
}

/* Fix responsive spacing in RTL */
.me-2 {
  margin-inline-end: 8px !important;
  margin-inline-start: 0 !important;
}

.ms-2 {
  margin-inline-start: 8px !important;
  margin-inline-end: 0 !important;
}

/* Fix card layouts for employee pages */
.v-card .v-card-text .v-row {
  direction: rtl;
}

/* Fix form field spacing */
.v-text-field,
.v-select,
.v-textarea,
.v-file-input {
  text-align: right;
  direction: rtl;
}

/* Fix balance cards layout */
.balance-info {
  direction: rtl;
  text-align: center;
}

/* Fix status cards in attendance view */
.v-card.pa-3 {
  direction: rtl;
  text-align: center;
}

/* Fix tabs alignment */
.v-tabs {
  direction: rtl;
}

.v-tab {
  text-align: right;
}

/* Fix stepper alignment */
.v-stepper {
  direction: rtl;
}

.v-stepper-header {
  direction: rtl;
}

.v-stepper-window {
  direction: rtl;
}

/* Fix expansion panels alignment */
.v-expansion-panels {
  direction: rtl;
}

.v-expansion-panel-title {
  text-align: right;
}

.v-expansion-panel-text {
  text-align: right;
}

/* Fix rating component alignment */
.v-rating {
  direction: rtl !important;
}

/* Fix snackbar alignment */
.v-snackbar {
  direction: rtl;
}

/* Fix toolbar alignment */
.v-toolbar {
  direction: rtl;
}

/* Fix menu alignment */
.v-menu .v-list {
  direction: rtl;
}

/* Fix pagination alignment */
.v-pagination {
  direction: rtl;
}

/* Fix breadcrumbs alignment */
.v-breadcrumbs {
  direction: rtl;
}

/* Fix card actions alignment */
.v-card-actions {
  direction: rtl;
}

/* Fix list groups for nested navigation */
.v-list-group {
  direction: rtl;
}

.v-list-group__items {
  padding-inline-start: 16px;
}

.v-list-group__header {
  padding-inline-start: 16px;
  padding-inline-end: 16px;
}

/* Fix date picker alignment */
.v-date-picker {
  direction: rtl;
}

/* Fix time picker alignment */
.v-time-picker {
  direction: rtl;
}

/* Fix switch alignment */
.v-switch {
  direction: rtl;
}

.v-switch .v-label {
  margin-inline-start: 12px;
  margin-inline-end: 0;
}

/* Fix radio button alignment */
.v-radio {
  direction: rtl;
}

.v-radio .v-label {
  margin-inline-start: 8px;
  margin-inline-end: 0;
}

/* Fix container and row alignment */
.v-container {
  direction: rtl !important;
}

.v-row {
  direction: rtl !important;
}

.v-col {
  direction: rtl !important;
}

/* Fix toolbar and app bar */
.v-toolbar {
  direction: rtl !important;
}

.v-app-bar {
  direction: rtl !important;
}

.v-toolbar-title {
  direction: rtl !important;
  text-align: center !important;
}

/* Fix all text alignment by default */
h1, h2, h3, h4, h5, h6, p, span, div {
  text-align: right;
}

/* Fix button text */
.v-btn__content {
  direction: rtl !important;
}

/* Fix spacer */
.v-spacer {
  flex: 1 1 auto;
}

/* Fix list items */
.v-list {
  direction: rtl !important;
}

.v-list-item {
  direction: rtl !important;
  text-align: right !important;
}

.v-list-item-title {
  direction: rtl !important;
  text-align: right !important;
}

.v-list-item-subtitle {
  direction: rtl !important;
  text-align: right !important;
}

/* Fix menu and overlays */
.v-overlay__content {
  direction: rtl !important;
}

.v-menu__content {
  direction: rtl !important;
}

/* Fix dialog specifically */
.v-dialog > .v-overlay__content > .v-card {
  direction: rtl !important;
}

/* Fix form elements */
.v-form {
  direction: rtl !important;
}

.v-input {
  direction: rtl !important;
}

.v-input__control {
  direction: rtl !important;
}

.v-input__slot {
  direction: rtl !important;
}

/* Fix all flex containers */
.d-flex {
  direction: rtl !important;
}

/* Fix table wrapper */
.v-table__wrapper {
  direction: rtl !important;
}

/* Fix pagination */
.v-pagination {
  direction: rtl !important;
}

.v-pagination__list {
  direction: rtl !important;
}

/* Ensure all vuetify components respect RTL */
.v-main {
  direction: rtl !important;
}

.v-main__wrap {
  direction: rtl !important;
}
</style>
